<template>
  <div>
    <button class="button outline" @click="open()" tabindex="0">
      <slot />
    </button>
  </div>
</template>
<script lang="ts">
import type * as filestack from 'filestack-js'
import { useSnackbar } from 'vue3-snackbar'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import { reportAppError } from '~/utility/sentry/apiErrorReporter'
import { FilestackError, FilestackValidationError } from '../FilestackError'
import { getFilestackLang } from '../filestackLocale'
import { DimentionsMetadata, ExtendedFileMetadata } from '../types'

@Component({
  setup() {
    const snackbar = useSnackbar()
    const { i18n } = useI18n()
    return { snackbar, locale: i18n.locale }
  },
  emits: ['imagesUploaded'],
})
class Filestack extends Vue {
  snackbar: ReturnType<typeof useSnackbar>
  locale: string

  options = {
    rootId: 'filestack-picker',
    fromSources: [
      'local_file_system',
      'googlephotos',
      'googledrive',
      'instagram',
      'facebook',
      'dropbox',
      'url',
      'box',
      'onedrive',
    ],
    minFiles: 1,
    maxFiles: 100,
    maxSize: this.CLAUDINARY_MAX_SIZE,
    accept: 'image/*',
    exposeOriginalFile: true,
    transformations: {
      crop: {
        force: false,
      },
      circle: false,
      rotate: true,
    },
  }

  client: filestack.Client

  invalidLargeFileNames: string[] = []
  invalidSmallFileNames: string[] = []

  get CLAUDINARY_MAX_SIZE() {
    return Math.floor(
      Number(useRuntimeConfig().public.cloudinaryMaximumFileSizeInBytes),
    )
  }

  get UPLOADED_IMAGE_MIN_LONG_EDGE() {
    return Math.floor(
      Number(useRuntimeConfig().public.uploadedImageMinimumLongEdgeInPixels),
    )
  }

  get UPLOADED_IMAGE_MIN_SHORT_EDGE() {
    return Math.floor(
      Number(useRuntimeConfig().public.uploadedImageMinimumShortEdgeInPixels),
    )
  }

  get apiKey() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- Runtime config public object is untyped
    return this.$config.public.filestackApiKey
  }

  async open() {
    try {
      await import('filestack-js').then(async (filestack) => {
        try {
          this.client = filestack.init(this.apiKey)
          await this.client
            .picker({
              ...this.options,
              lang: getFilestackLang(this.locale),
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Filestack callback accepts async function
              onUploadDone: this.onUploadDone,
              onFileSelected: this.onFileSelected,
            })
            .open()
        } catch (error) {
          const filestackError = new FilestackError(
            'client initialization',
            undefined,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Error caught in try-catch
            { apiKey: this.apiKey },
            error,
          )

          reportAppError(filestackError, 'Filestack client initialization', {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Runtime config public object is untyped
            apiKeyLength: this.apiKey?.length || 0,
            hasApiKey: !!this.apiKey,
            userAgent: navigator.userAgent,
            pickerOptions: JSON.stringify(this.options),
            locale: this.locale,
          })

          throw filestackError
        }
      })
    } catch (error) {
      const filestackError = new FilestackError(
        'module import',
        undefined,
        { moduleUrl: 'filestack-js/build/browser/filestack.esm.js' },
        error,
      )

      reportAppError(filestackError, 'Filestack module import', {
        userAgent: navigator.userAgent,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Navigator connection API not fully typed
        connectionType: (navigator as any).connection?.effectiveType,
        onLine: navigator.onLine,
      })

      throw filestackError
    }
  }

  async onUploadDone({ filesUploaded, filesFailed }: filestack.PickerResponse) {
    try {
      await this.notifyImagesUploaded({ filesUploaded, filesFailed })
    } catch (error) {
      const filestackError = new FilestackError(
        'upload notification',
        undefined,
        {
          filesUploadedCount: filesUploaded?.length || 0,
          filesFailedCount: filesFailed?.length || 0,
        },
        error,
      )

      reportAppError(filestackError, 'Filestack upload notification', {
        uploadedFiles: filesUploaded?.map((f) => ({
          filename: f.filename,
          handle: f.handle,
        })),
        failedFiles: filesFailed?.map((f) => ({
          filename: f.filename,
          source: f.source,
        })),
      })

      throw filestackError
    }
  }

  onFileSelected(file: filestack.PickerFileMetadata) {
    return new Promise((resolve, reject) => {
      if (file.size > this.CLAUDINARY_MAX_SIZE) {
        const error = new FilestackValidationError(
          'file-too-large',
          file.filename,
          this.CLAUDINARY_MAX_SIZE,
          file.size,
          file,
        )

        reportAppError(error, 'Filestack file size validation', {
          maxSizeBytes: this.CLAUDINARY_MAX_SIZE,
          maxSizeMB: Math.round(this.CLAUDINARY_MAX_SIZE / (1024 * 1024)),
          actualSizeBytes: file.size,
          actualSizeMB: Math.round(file.size / (1024 * 1024)),
          source: file.source || 'unknown',
        })

        this.invalidLargeFileNames.push(file.filename)
        this.snackbar.add({
          text: this.$t(
            'Selected photo is too big: {fileName}. Maximum photo size is {fileSize}',
            {
              fileNames: this.invalidLargeFileNames.join(', '),
              fileSize: 40,
              invalidFiles: this.invalidLargeFileNames.length,
            },
          ),
          type: 'success',
          dismissible: false,
        })
        this.invalidLargeFileNames = []
        this.$gtm.push({ event: 'filestack_invalid_image_size_too_big' })
        reject(error)
      }

      if (!file.originalFile) {
        const error = new FilestackValidationError(
          'no-original-file',
          file.filename,
          undefined,
          undefined,
          file,
        )

        reportAppError(error, 'Filestack file selection', {
          source: file.source || 'unknown',
          fileType: file.mimetype,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Filestack client session is untyped
          uploadSessionId: this.client?.session?.id,
          userAgent: navigator.userAgent,
        })

        this.$gtm.push({ event: 'filestack_no_original_file' })
        reject(error)
        return
      }

      const blob = file.originalFile

      // Get an object URL for the local file
      const url = URL.createObjectURL(blob)

      // Create an image and load the object URL
      const img = new Image()
      img.src = url

      img.onload = () => {
        URL.revokeObjectURL(url)
        // dimensions come through off by one, so perfect 1350*1800 images got rejected
        if (this.imageTooSmall(img.naturalWidth + 1, img.naturalHeight + 1)) {
          const expectedDimension = Math.max(
            this.UPLOADED_IMAGE_MIN_LONG_EDGE,
            this.UPLOADED_IMAGE_MIN_SHORT_EDGE,
          )
          const actualDimension = Math.max(img.naturalWidth, img.naturalHeight)

          const error = new FilestackValidationError(
            'file-too-small',
            file.filename,
            expectedDimension,
            actualDimension,
            file,
          )

          reportAppError(error, 'Filestack image dimension validation', {
            actualWidth: img.naturalWidth,
            actualHeight: img.naturalHeight,
            minLongEdge: this.UPLOADED_IMAGE_MIN_LONG_EDGE,
            minShortEdge: this.UPLOADED_IMAGE_MIN_SHORT_EDGE,
            source: file.source || 'unknown',
          })

          this.invalidSmallFileNames.push(file.filename)
          this.snackbar.add({
            text: this.$t(
              "Selected photo is too small: {fileName}, and doesn't meet our quality guidelines, please upload the original. The minimum size for photos is {minimumWidth} x {minimumHeight} pixels.",
              {
                fileNames: this.invalidSmallFileNames.join(', '),
                invalidFiles: this.invalidSmallFileNames.length,
                minimumWidth: this.UPLOADED_IMAGE_MIN_LONG_EDGE,
                minimumHeight: this.UPLOADED_IMAGE_MIN_SHORT_EDGE,
              },
            ),
            type: 'success',
            dismissible: false,
          })
          this.invalidSmallFileNames = []
          this.$gtm.push({ event: 'filestack_invalid_image_size_too_small' })
          reject(error)
        } else {
          resolve(true)
        }
      }

      img.onerror = (error) => {
        URL.revokeObjectURL(url)

        const filestackError = new FilestackValidationError(
          'image-load-failed',
          file.filename,
          undefined,
          undefined,
          file,
        )

        reportAppError(filestackError, 'Filestack image load for validation', {
          source: file.source || 'unknown',
          fileType: file.mimetype,
          fileSize: file.size,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Error event from Image element is untyped
          originalError: error?.toString?.() ?? String(error),
        })

        this.$gtm.push({ event: 'filestack_image_load_error' })
        reject(filestackError)
      }
    })
  }

  private imageTooSmall(width: number, height: number): boolean {
    // valid portrait
    if (
      width > this.UPLOADED_IMAGE_MIN_SHORT_EDGE &&
      height > this.UPLOADED_IMAGE_MIN_LONG_EDGE
    ) {
      return false
    }
    // valid landscape
    if (
      width > this.UPLOADED_IMAGE_MIN_LONG_EDGE &&
      height > this.UPLOADED_IMAGE_MIN_SHORT_EDGE
    ) {
      return false
    }
    return true
  }

  private async notifyImagesUploaded({
    filesUploaded,
    filesFailed,
  }: {
    filesUploaded: filestack.PickerFileMetadata[]
    filesFailed: filestack.PickerFileMetadata[]
  }) {
    const filesUploadedExtended = await this.extendMetadata(filesUploaded)
    this.$emit('imagesUploaded', {
      filesUploaded: filesUploadedExtended,
      filesFailed,
    })
  }

  private async extendMetadata(
    files: filestack.PickerFileMetadata[],
  ): Promise<ExtendedFileMetadata[]> {
    try {
      const filesMetadata = await Promise.all(
        files.map((file) => this.getDimentionsMetadata(file.handle)),
      )
      return files.map((file, index) => ({
        ...file,
        ...filesMetadata[index],
      }))
    } catch (error) {
      const filestackError = new FilestackError(
        'metadata extension',
        undefined,
        { fileCount: files?.length || 0 },
        error,
      )

      reportAppError(filestackError, 'Filestack metadata extension', {
        fileHandles: files?.map((f) => f.handle),
        fileCount: files?.length || 0,
      })

      throw filestackError
    }
  }

  getDimentionsMetadata(filestackHandle: string): Promise<DimentionsMetadata> {
    try {
      return this.client.metadata(filestackHandle, {
        height: true,
        width: true,
      })
    } catch (error) {
      const filestackError = new FilestackError(
        'metadata retrieval',
        undefined,
        { handle: filestackHandle },
        error,
      )

      reportAppError(filestackError, 'Filestack dimensions metadata', {
        handle: filestackHandle,
        metadataOptions: { height: true, width: true },
      })

      throw filestackError
    }
  }
}

export default toNative(Filestack)
</script>
<style>
#filestack-picker {
  position: relative;
  z-index: 1;
}

.fsp-picker {
  font-size: 14px;
}

.fsp-drop-area__button {
  box-shadow: none;
}
</style>
